import * as React from "react";
import { useState, useCallback } from "react";
import styled from "styled-components";
import useProductName from "brand-customization/useProductName";
import HighlightingRulesContext from "views/components/contexts/HighlightingRulesContext";
import { DocumentTitle, Spinner, Link } from "components/common";
import PageContentLayout from "components/layout/PageContentLayout";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import useActiveSearch from "data-lake/preview/hooks/useActiveSearch";
import DataLakePreviewStoreProvider from "data-lake/preview/store/DataLakePreviewStoreProvider";
import useUrlParams from "data-lake/preview/hooks/useUrlParams";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import Panel from "components/bootstrap/Panel";
import DataLakeRoutes from "data-lake/Routes";
import useLicenseValidityForSubject from "license/hooks/useLicenseValidityForSubject";
import { LICENSE_SUBJECTS } from "license/constants";
import { InvalidLicenseWarning } from "license/LicenseCheck";
import Sidebar from "views/components/sidebar/Sidebar";
import DataLakeHighlightingRules from "data-lake/preview/DataLakeHighlightingRules";
import useActiveDataLakes from "data-lake/preview/hooks/useActiveDataLakes";
import PreviewConfiguredDataLakesProvider from "data-lake/preview/contexts/PreviewConfiguredDataLakesProvider";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import usePluginEntities from "hooks/usePluginEntities";
import FieldTypesProvider from "data-lake/preview/contexts/FieldTypesProvider";
import findDataLakeIdInSearchType from "data-lake/preview/utils/findDataLakeIdInSearchType";
import useFeature from "hooks/useFeature";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import AppConfig from "util/AppConfig";
import { DATA_LAKE_INTERNAL_ENABLED } from "data-lake/Constants";
const initialSearchForDataLake = (selectedDataLakeId, activeSearch) => {
  if (!selectedDataLakeId || !activeSearch) return void 0;
  const activeSearchDataLakeId = findDataLakeIdInSearchType(activeSearch);
  if (activeSearchDataLakeId === selectedDataLakeId && activeSearch) return activeSearch;
  return void 0;
};
const PreviewPageContent = ({
  activeSearch,
  initialUrlParams,
  searchJob,
  resetUrlQueryParams
}) => {
  const { selectedDataLake } = useSelectedDataLake();
  const dataLakePlugins = usePluginEntities("dataLakeStorageTypes");
  const dataLakePlugin = dataLakePlugins.find(({ storageType }) => storageType === (selectedDataLake == null ? void 0 : selectedDataLake.storageType));
  const PageComponent = dataLakePlugin == null ? void 0 : dataLakePlugin.previewComponent;
  const initialSearch = initialSearchForDataLake(selectedDataLake == null ? void 0 : selectedDataLake.id, activeSearch);
  if (!PageComponent) {
    throw new Error(`No page component found for data lake storage type: ${selectedDataLake == null ? void 0 : selectedDataLake.storageType}`);
  }
  return /* @__PURE__ */ React.createElement(
    PageComponent,
    {
      key: selectedDataLake == null ? void 0 : selectedDataLake.id,
      initialSearch,
      initialUrlParams,
      initialSearchJob: searchJob,
      resetUrlQueryParams
    }
  );
};
const StyledPageContentLayout = styled(PageContentLayout)`
  .page-content-grid {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: auto;
  }
`;
const GridContainer = styled.div`
  display: flex;
  height: 100%;
`;
const DataLakePreviewPageComponent = ({ activeDataLakes }) => {
  const { initialUrlParams, resetUrlQueryParams, setDataLakeIdUrlParam } = useUrlParams();
  const { config } = useSearchConfiguration();
  const [highlightingRules, setHighlightingRules] = useState([]);
  const highlightSidebarSection = useCallback(
    () => /* @__PURE__ */ React.createElement(DataLakeHighlightingRules, { setHighlightingRules }),
    []
  );
  const {
    search: activeSearch,
    result: activeSearchResult,
    searchJob,
    isLoading: isLoadingActiveSearch
  } = useActiveSearch(initialUrlParams);
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: "Data Lake Preview" }, config && !isLoadingActiveSearch ? /* @__PURE__ */ React.createElement(
    PreviewConfiguredDataLakesProvider,
    {
      setDataLakeIdUrlParam,
      initialUrlParams,
      activeSearch,
      searchJobStatus: searchJob.status,
      activeDataLakes
    },
    /* @__PURE__ */ React.createElement(DataLakePreviewStoreProvider, { result: activeSearchResult }, /* @__PURE__ */ React.createElement(FieldTypesProvider, null, /* @__PURE__ */ React.createElement(HighlightingRulesContext.Provider, { value: highlightingRules }, /* @__PURE__ */ React.createElement(GridContainer, null, /* @__PURE__ */ React.createElement(
      Sidebar,
      {
        sections: [
          {
            key: "highlighting",
            icon: "format_paragraph",
            title: "Highlighting",
            content: highlightSidebarSection
          }
        ],
        enableSidebarPinning: false,
        actions: [],
        title: "Data Lake Preview"
      }
    ), /* @__PURE__ */ React.createElement(StyledPageContentLayout, null, /* @__PURE__ */ React.createElement(
      PreviewPageContent,
      {
        activeSearch,
        initialUrlParams,
        searchJob,
        resetUrlQueryParams
      }
    ))))))
  ) : /* @__PURE__ */ React.createElement(Spinner, null));
};
const LicenseCheck = ({ children }) => {
  const {
    data: { valid: isValidLicense },
    isInitialLoading: isLoadingLicense
  } = useLicenseValidityForSubject(LICENSE_SUBJECTS.enterprise);
  if (isLoadingLicense) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (!isValidLicense) {
    return /* @__PURE__ */ React.createElement(PageContentLayout, null, /* @__PURE__ */ React.createElement(
      InvalidLicenseWarning,
      {
        title: "Data Lake Preview is disabled",
        featureName: "Data Lake Preview",
        licenseSubject: LICENSE_SUBJECTS.enterprise,
        displayWarningContainer: true,
        bsStyle: "danger"
      }
    ));
  }
  return children;
};
const SetupInstructions = ({
  hasPermissions,
  externalEnabled,
  internalEnabled
}) => {
  const productName = useProductName();
  if (!hasPermissions) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "Contact your Administrator to set it up.");
  }
  const isCloud = AppConfig.isCloud();
  if (externalEnabled && internalEnabled) {
    return isCloud ? /* @__PURE__ */ React.createElement(React.Fragment, null, "You can do that ", /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.CONNECTORS }, "here"), " for a connector with an external Data Lake, or contact ", productName, " support for the internal Data Lake.") : /* @__PURE__ */ React.createElement(React.Fragment, null, "You can do that ", /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.BACKEND }, "here"), " for an internal Data Lake or", " ", /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.CONNECTORS }, "here"), " for a connector with an external Data Lake.");
  }
  if (internalEnabled) {
    return isCloud ? /* @__PURE__ */ React.createElement(React.Fragment, null, "Contact ", productName, " support to set up the internal Data Lake.") : /* @__PURE__ */ React.createElement(React.Fragment, null, "You can do that ", /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.BACKEND }, "here"), ".");
  }
  if (externalEnabled) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "You can do that ", /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.CONNECTORS }, "here"), ".");
  }
  return null;
};
const ActiveBackendCheck = ({ children }) => {
  const currentUser = useCurrentUser();
  const {
    data: activeDataLakes,
    isInitialLoading: isInitialLoadingActiveDataLakes,
    hasActiveDataLake
  } = useActiveDataLakes();
  const hasConfigPermissions = isPermitted(currentUser.permissions, "data_lake_config:update");
  const externalDLFeatureIsEnabled = useFeature("external_data_lake_search");
  const internalDLFeatureIsEnabled = DATA_LAKE_INTERNAL_ENABLED;
  if (isInitialLoadingActiveDataLakes) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (!hasActiveDataLake) {
    return /* @__PURE__ */ React.createElement(PageContentLayout, null, /* @__PURE__ */ React.createElement(Panel, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Panel.Heading, null, "There is no active Data Lake backend or connector"), /* @__PURE__ */ React.createElement(Panel.Body, null, /* @__PURE__ */ React.createElement(React.Fragment, null, "To access Data Lake Preview, a Data Lake must be configured in Graylog.", " ", /* @__PURE__ */ React.createElement(
      SetupInstructions,
      {
        hasPermissions: hasConfigPermissions,
        externalEnabled: externalDLFeatureIsEnabled,
        internalEnabled: internalDLFeatureIsEnabled
      }
    ), " ", "For more details, see the ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.DATA_LAKE, text: "documentation" }), "."))));
  }
  return children({ activeDataLakes });
};
const DataLakePreviewPage = () => /* @__PURE__ */ React.createElement(LicenseCheck, null, /* @__PURE__ */ React.createElement(ActiveBackendCheck, null, ({ activeDataLakes }) => /* @__PURE__ */ React.createElement(DataLakePreviewPageComponent, { activeDataLakes })));
export default DataLakePreviewPage;
